import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { CircularProgress, Grid, ThemeProvider, createTheme } from "@mui/material";
import { observer } from "mobx-react";
import rootStores from "./stores";
import { getAuthData } from "./fetchers/authentication";
import Api from "./api/apiClient";
import NavBar from "./components/nav-bar/NavBar";
import LoginComponent from "./pages/login/LoginComponent";
import TicketsCompareComponent from "./pages/ticketsCompare/TicketsCompareComponent";
import NewClubComponent from "./pages/newClub/NewClubComponent";
import RenewSeasonComponent from "./pages/renewSeason/RenewSeasonComponent";
import SendEmailComponent from "./pages/sendEmail/SendEmailComponent";
import Errors from "./pages/errors/Errors";
import TicketViewer from "./pages/ticketViewer/TicketViewerComponent";
import i18n from "./i18n";


const authStore = rootStores.AuthStore;

Api.interceptors.response.use(
  (response) => response,
  (error) => {
    const statusCode = error?.response?.data?.statusCode;
    if (statusCode === 403 || statusCode === 401) {
      authStore.setAuthenticated(false);
    }
    return error?.message;
  }
);

const theme = createTheme({ typography: { fontFamily : 'Rubik'}, palette :  {primary : {main : "#5C6BC0"}} });

const App: React.FC = observer(() => {
  const { authenticated, setAuthenticated, setUserName, setClubPrefix } = authStore;
  const [isLoading, setIsLoading] = useState(true);
  const [withoutNavigation, setWithoutNavigation] = useState(false);
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    const savedLang = (localStorage.getItem("language") as "he" | "en") || "en";
    i18n.changeLanguage(savedLang).then(() => {
      document.documentElement.dir = savedLang === "he" ? "ltr" : "rtl";
      setI18nReady(true);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const res = await getAuthData();
      if (res?.status === 200) {
        setUserName(res?.data?.name);
        setClubPrefix(res?.data.clubPrefix);
        setAuthenticated(true);
      } else setAuthenticated(false);
      setIsLoading(false);
    })();
  }, [setAuthenticated, setUserName]);

  const RedirectLogin = () => <Navigate to="/login"/>;

  const AuthGuard = () => {
    const location = useLocation();
    const pathname = location.pathname;
    const isPublicRoute = pathname.includes("tickets");
    if (isPublicRoute) {
      setIsLoading(false);
      setWithoutNavigation(true);
      return <></>;
    } else if (!authenticated) return <RedirectLogin />;
    else return <></>;
  };

  const Layout = (): JSX.Element => (
    <>
      <AuthGuard />
      <div className="main-container">
        {!withoutNavigation && <NavBar />}
        <Outlet />
      </div>
    </>
  );

  const LoadingSpinner = () => (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: "50vh" }}
    >
      <CircularProgress color="secondary" />
    </Grid>
  );

  if (!i18nReady || isLoading) return <LoadingSpinner />;

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginComponent />} />
          <Route path="/" element={<Layout />}>
            <Route path="/" element={<TicketsCompareComponent />} />
            <Route path="/Errors" element={<Errors />} />
            <Route path="/newClub" element={<NewClubComponent />} />
            <Route path="/TicketsCompare" element={<TicketsCompareComponent />} />
            <Route path="/RenewSeason" element={<RenewSeasonComponent />} />
            <Route path="/SendEmail" element={<SendEmailComponent />} />
            <Route path="/tickets/:eventId/:ticketToken" element={<TicketViewer />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
});

export default App;
